import { useState } from "react";
import { useSearchParams } from "react-router";
import { Navigate, NavLink, Outlet, useNavigate } from "react-router";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";

import { DestinationType } from "@prequel/react";
import {
  Button,
  ButtonStyle,
  checkTabIsActive,
  MenuItemStyle,
  Modal,
  PageHeading,
  Spinner,
  Tabs,
  Toggle,
} from "@prequel/shared-components";

import SyncSpecificModelsModal from "../SyncSpecificModelsModal";

import { useDestination, useUpdateDestination } from "@/api/queries/destinations";
import { usePostTransfer } from "@/api/queries/transfers";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";
import BufoPage from "@/pages/BufoPage";
import { isBufoUUID } from "@/pages/BufoPage/bufo";

// Main wrapper for destination view with tabs
export const DestinationPageWrapper = () => {
  const destinationID = useRequiredParam("destinationID");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showSelectiveSyncModal, setShowSelectiveSyncModal] = useState(false);
  const updateDestinationMutation = useUpdateDestination();
  const postTransferMutation = usePostTransfer();
  const [searchParams, setSearchParams] = useSearchParams();
  const isRefreshing = useIsFetching({
    queryKey: ["destination-activities", destinationID],
  });

  const { data: destination, isLoading } = useDestination(destinationID);

  const reloadActivity = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("cursor");
    params.delete("page_size");
    setSearchParams(params);
    queryClient.invalidateQueries({
      queryKey: ["destination-activities", destinationID],
    });
  };

  const outletContext = {
    destination,
    destinationID,
    destinationName: destination?.name,
    reloadActivity,
  };

  const tabs = [
    <NavLink key="Activity" to={`/destinations/${destinationID}`} className={checkTabIsActive} end>
      Activity
    </NavLink>,
    <NavLink key="Details" to={`/destinations/${destinationID}/details`} className={checkTabIsActive} end>
      Details
    </NavLink>,
    <NavLink key="Connection" to={`/destinations/${destinationID}/connection`} className={checkTabIsActive} end>
      Connection
    </NavLink>,
    <NavLink key="Admin" to={`/destinations/${destinationID}/admin`} className={checkTabIsActive} end>
      Admin
    </NavLink>,
  ];

  const syncButtonMenuItems = [
    {
      label: "Select models to sync",
      style: MenuItemStyle.PRIMARY,
      onClick: () => setShowSelectiveSyncModal(true),
    },
  ];

  // Check for easter egg before rendering
  if (isBufoUUID(destinationID)) {
    return <BufoPage />;
  }

  if (isLoading) {
    return <Spinner />;
  }

  if (!destination) {
    return <Navigate to="/destinations" replace />;
  }

  const onSyncNow = () => {
    postTransferMutation.mutate(
      { destinationID: destination.id, fullRefresh: false },
      {
        onSuccess: () => {
          reloadActivity();
        },
      },
    );
  };

  const setEnabled = (isEnabled: boolean) =>
    updateDestinationMutation.mutate({
      destinationID: destination.id,
      fields: { is_enabled: isEnabled },
    });

  return (
    <div className="max-w-6xl px-7 py-4">
      {destination.type === DestinationType.ExistingDestination && (
        <Modal open={showSelectiveSyncModal} onClose={() => setShowSelectiveSyncModal(false)}>
          <SyncSpecificModelsModal
            destination={destination}
            fullRefresh={false}
            onCancel={() => setShowSelectiveSyncModal(false)}
            onConfirm={() => setShowSelectiveSyncModal(false)}
          />
        </Modal>
      )}
      {/* /* eslint-enable */}
      <PageHeading
        title={destination.name}
        onGoBack={() => navigate("/destinations")}
        breadcrumbs={<Breadcrumbs dynamicLabel={destination.name} />}
      >
        <Button
          text={
            postTransferMutation.isPending ? (
              <div className="flex">
                <Spinner.Inline className="mr-3 text-emerald-600" /> Starting Sync
              </div>
            ) : (
              "Sync Now"
            )
          }
          type={ButtonStyle.TERTIARY}
          onClick={onSyncNow}
          menuItems={destination.type === DestinationType.ExistingDestination ? syncButtonMenuItems : undefined}
        />
        <Toggle label="Enabled" checked={destination.is_enabled} setChecked={setEnabled} />
      </PageHeading>
      <div className="flex-1">
        <div className="relative mx-auto">
          <div className="pt-0 pb-16">
            <div className="pb-6">
              {/* Tabs */}
              <Tabs tabs={tabs} isLoading={!!isRefreshing} onRefresh={reloadActivity} />
              <Outlet context={outletContext} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrapper for edit view with narrow layout
export const DestinationEditWrapper = () => {
  const destinationID = useRequiredParam("destinationID");
  const navigate = useNavigate();
  const { data: destination, isLoading } = useDestination(destinationID);

  if (isLoading) {
    return <Spinner />;
  }

  if (!destination) {
    return <Navigate to="/destinations" replace />;
  }

  return (
    <div className="max-w-xl px-7 py-4">
      <PageHeading
        title="Edit Destination"
        onGoBack={() => navigate(`/destinations/${destinationID}`)}
        breadcrumbs={<Breadcrumbs dynamicLabel={destination.name} />}
      />
      <Outlet />
    </div>
  );
};

// Simple wrapper for transfer/integrity detail views
const DestinationPage = () => {
  const destinationID = useRequiredParam("destinationID");
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: destination, isLoading } = useDestination(destinationID);

  const reloadActivity = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("cursor");
    params.delete("page_size");
    setSearchParams(params);

    queryClient.invalidateQueries({
      queryKey: ["destination-activities", destinationID],
    });
  };

  const outletContext = {
    destination,
    destinationID,
    destinationName: destination?.name,
    reloadActivity,
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (!destination) {
    return <Navigate to="/destinations" replace />;
  }

  return <Outlet context={outletContext} />;
};

export default DestinationPage;
